import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, MessageCircle, ExternalLink } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, SERVICES, STEPS } from "./cebugoldData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const GREEN = "#0F3D2E";
const GOLD = "#C9A227";

const fadeUp = { initial: { opacity: 0, y: 22 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease: "easeOut" } };

export default function CebugoldSite() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("jewelry");
  const links = [["About", "#about"], ["Jewelry", "#products"], ["Services", "#services"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen bg-white" style={{ color: "#141E19", fontFamily: "'Inter var', sans-serif" }} data-testid="cebugold-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant:wght@600;700&family=Mulish:wght@400;500;600;700&display=swap'); .cg-serif{font-family:'Cormorant',serif;} .cg-body{font-family:'Mulish',sans-serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs cg-body py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: GOLD }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-white shadow-sm">
        <nav className="max-w-6xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="cebugold-logo" className="cg-serif text-2xl font-bold" style={{ color: GREEN }}>Gold &amp; Glitz</a>
          <div className="hidden md:flex items-center gap-8 cg-body">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium" style={{ color: "#3A5246" }}>{l}</a>)}
          </div>
          <a href="#visit" data-testid="cebugold-header-cta" className="hidden sm:inline-flex cg-body px-6 py-2.5 text-sm font-semibold text-white" style={{ background: GREEN }}>Visit Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="cebugold-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-white cg-body">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — established institution, real branded product macro */}
      <section className="relative min-h-[80vh] flex items-center" data-testid="cebugold-hero">
        <img src={BIZ.logoShot} alt="Gold & Glitz fine jewelry by Cebu Gold Pacific" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center 65%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,61,46,0.88) 0%, rgba(15,61,46,0.55) 55%, rgba(15,61,46,0.05) 92%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 w-full">
          <motion.div {...fadeUp} className="max-w-xl">
            <span className="cg-body text-xs uppercase tracking-[0.25em]" style={{ color: GOLD }}>Established 1997 · Osmeña Blvd., Cebu City</span>
            <h1 className="mt-5 text-5xl sm:text-6xl cg-serif font-bold text-white leading-[1.05]">Fine jewelry, since 1997.</h1>
            <p className="mt-5 cg-body text-white/75 max-w-md">Gold and diamond settings, customization and handcrafted pieces from Cebu Gold Pacific's jewelry brand.</p>
            <div className="mt-8 flex flex-wrap gap-4 cg-body">
              <a href="#products" data-testid="cebugold-hero-cta" className="px-8 py-3.5 text-sm font-semibold" style={{ background: GOLD, color: GREEN }}>Explore Our Collection</a>
              <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="cebugold-hero-message" className="px-8 py-3.5 text-sm font-semibold text-white border border-white/40">Message Us</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-4xl mx-auto px-5 sm:px-8 py-20 text-center">
        <motion.p {...fadeUp} className="text-2xl sm:text-3xl cg-serif leading-relaxed" style={{ color: GREEN }}>{BIZ.intro}</motion.p>
        <motion.div {...fadeUp} className="mt-12 grid sm:grid-cols-4 gap-6 cg-body">
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="text-center">
              <p className="font-semibold text-sm" style={{ color: GREEN }}>{h.title}</p>
              <p className="mt-1.5 text-xs text-[#3A5246]/75">{h.text}</p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="py-20" style={{ background: "#F2EDE0" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="cg-serif text-4xl font-bold text-center mb-12" style={{ color: GREEN }}>Our Jewelry</motion.h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="cebugold-product-card" className="bg-white shadow-sm group">
                <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-4 cg-body">
                  <h3 className="text-sm font-semibold">{p.name}</h3>
                  <p className="mt-3 text-xs font-semibold" style={{ color: "#8A6B0A" }}>Ask About This Piece &rarr;</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TAB SWITCHER — the institution's multi-service identity */}
      <section id="services" className="max-w-4xl mx-auto px-5 sm:px-8 py-20">
        <motion.h2 {...fadeUp} className="cg-serif text-4xl font-bold text-center mb-10" style={{ color: GREEN }}>What We Offer</motion.h2>
        <div className="flex justify-center gap-2 mb-10 cg-body">
          {SERVICES.map((s) => (
            <button key={s.key} data-testid={`cebugold-tab-${s.key}`} onClick={() => setTab(s.key)} className="px-5 py-2.5 text-sm font-semibold rounded-full transition-colors" style={tab === s.key ? { background: GREEN, color: "white" } : { background: "#EEF2EF", color: "#3A5246" }}>{s.label}</button>
          ))}
        </div>
        {SERVICES.filter((s) => s.key === tab).map((s) => (
          <motion.div key={s.key} {...fadeUp} className="text-center max-w-xl mx-auto">
            <h3 className="cg-serif text-2xl font-semibold" style={{ color: GREEN }}>{s.title}</h3>
            <p className="mt-3 cg-body text-[#3A5246]/80">{s.text}</p>
          </motion.div>
        ))}
      </section>

      {/* STEPS */}
      <section className="py-16 cg-body" style={{ background: GREEN }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 grid sm:grid-cols-4 gap-6 text-center">
          {STEPS.map((s) => (
            <motion.div key={s.n} {...fadeUp}>
              <div className="text-2xl font-bold" style={{ color: GOLD }}>{s.n}</div>
              <p className="mt-2 font-semibold text-white text-sm">{s.title}</p>
              <p className="mt-1 text-xs text-white/60">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-12">
        <motion.div {...fadeUp}>
          <h2 className="cg-serif text-4xl font-bold" style={{ color: GREEN }}>Visit Us</h2>
          <ul className="mt-8 space-y-5 cg-body">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: GREEN }} className="mt-0.5" />{BIZ.address}</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: GREEN }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/[()\s-]/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: GREEN }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="cebugold-facebook-link" className="hover:underline">Goldandglitzph</a></li>
            <li className="flex items-start gap-4"><ExternalLink size={20} style={{ color: GREEN }} className="mt-0.5" /><a href={BIZ.officialSite} target="_blank" rel="noopener noreferrer" data-testid="cebugold-official-link" className="hover:underline">cebugoldpacific.com</a></li>
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="min-h-[320px] rounded-xl overflow-hidden shadow-md"><iframe title="Gold & Glitz map" src={BIZ.mapEmbed} className="w-full h-full min-h-[320px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></motion.div>
      </section>

      <footer className="py-8 bg-white border-t cg-body" style={{ borderColor: "#E4E9E5" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs" style={{ color: "#5A6E62" }}>
          <p className="cg-serif text-base font-semibold" style={{ color: GREEN }}>Gold &amp; Glitz — Cebu Gold Pacific</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="cebugold-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 cg-body px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: GREEN }}><MessageCircle size={17} /> Message Gold &amp; Glitz</a>
      <ConceptCTAPill slug="cebu-gold-pacific-concept" />
    </div>
  );
}
